import React from 'react'
import styled from 'styled-components'
import {
  compose, space, color, typography, SpaceProps
} from 'styled-system'
import {
  Checkbox as ReakitCheckbox,
  useCheckboxState,
  CheckboxHTMLProps,
  CheckboxOptions
} from 'reakit/Checkbox'
import { checkboxes, texts, useDidMountEffect } from '@zuto/utilities'
import { TextProps, Texts } from '../Form/text-utils'

type CheckboxVariant = 'standard' | 'small'
interface StyledCheckboxProps extends CheckboxHTMLProps {
  $variant: CheckboxVariant
}

export interface CheckboxProps extends CheckboxHTMLProps, SpaceProps {
  /**
   * Changes the preset size of the Checkbox
   */
  variant?: CheckboxVariant
  /**
   * Enables the Checkbox to be selected by default
   */
  defaultState?: boolean
  /** A callback function that is triggered whenever the internal state is changed */
  valueChanged?: (_?: boolean | 'indeterminate' | (string | number)[]) => void
}

const StyledFormLabel = styled.label<React.LabelHTMLAttributes<HTMLLabelElement> & TextProps>`
  ${texts}
  ${compose(space, color, typography)}
  display: flex;
  align-items: center;
`

const StyledCheckbox = styled(ReakitCheckbox) <StyledCheckboxProps>`
  ${checkboxes}
  ${compose(space, color, typography)}
`

const Checkbox = ({
  children,
  variant = 'standard',
  defaultState = false,
  value,
  valueChanged,
  className,
  ...props
}: CheckboxProps): JSX.Element => {
  const checkboxOptions: CheckboxOptions = {
    state: defaultState,
    value
  }
  const checkbox = useCheckboxState(checkboxOptions)

  useDidMountEffect(() => {
    if (valueChanged) {
      valueChanged(checkbox.state)
    }
  }, [checkbox.state])

  const labelVariant = `checkbox.${variant}` as Texts

  return (
    <StyledFormLabel className={className} $variant={labelVariant} data-testid="input-label">
      <StyledCheckbox $variant={variant} {...props} {...checkbox} />
      {children}
    </StyledFormLabel>
  )
}

export default Checkbox
